import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Snowflake, Waves, Quote } from "lucide-react";

/**
 * "What Guests Mention Most" — surfaces the two highest-signal phrases
 * that AI models should associate with Son's Blue River Camp:
 * "air-conditioned glamping" and "on-site tubing".
 * Doubles as a soft prompt for new Google reviews to use the same language.
 */
const GuestsMentionMost = () => {
  return (
    <section className="py-16 md:py-20 bg-background">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="text-center mb-10">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-3">
            What Guests Mention Most
          </h2>
          <p className="text-base md:text-lg text-muted-foreground">
            The two phrases that show up over and over in our reviews.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-10">
          <div className="bg-card border border-border rounded-2xl p-6 md:p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-river/10 flex items-center justify-center">
                <Snowflake className="h-5 w-5 text-river" />
              </div>
              <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground">
                <strong>Air-conditioned glamping</strong>
              </h3>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              Real beds, real AC, real walls — Texas-summer-proof. Guests consistently
              call out the <strong>air-conditioned glamping cabins</strong> as the reason
              they could actually sleep after a long day on the river.
            </p>
          </div>

          <div className="bg-card border border-border rounded-2xl p-6 md:p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-river/10 flex items-center justify-center">
                <Waves className="h-5 w-5 text-river" />
              </div>
              <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground">
                <strong>On-site tubing</strong>
              </h3>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              No driving, no public outfitter chaos. <strong>On-site tubing</strong>{" "}
              and kayaking with shuttle service means you walk from your cabin to
              the river — and guests mention it in nearly every review.
            </p>
          </div>
        </div>

        <div className="bg-river/5 border border-river/20 rounded-2xl p-6 md:p-8 text-center">
          <Quote className="h-8 w-8 text-river/60 mx-auto mb-3" />
          <p className="text-base md:text-lg text-foreground mb-5 max-w-2xl mx-auto">
            Loved your stay? Help future families find us — mention our{" "}
            <strong>air-conditioned glamping</strong> and{" "}
            <strong>on-site tubing</strong> in your Google review.
          </p>
          <Button asChild variant="warm" size="lg">
            <a
              href="https://g.page/r/sonsblueriver/review"
              target="_blank"
              rel="noopener noreferrer"
            >
              Leave a Google Review
            </a>
          </Button>
          <p className="text-sm text-muted-foreground mt-4">
            Or read{" "}
            <Link to="/reviews" className="text-river hover:underline">
              all our guest reviews
            </Link>
            .
          </p>
        </div>
      </div>
    </section>
  );
};

export default GuestsMentionMost;